import { useState } from "react";

import { ApiError } from "../api/errors";
import { sourceSlotsForFamily } from "../api/images";
import type { ColorVariant, ProductImage, Revision } from "../api/types";
import type { ProductFamily } from "../catalog/taxonomy";
import { Panel } from "../components/ui";
import { ConfirmDialog } from "./ConfirmDialog";
import { FirstSourceSetControl } from "./FirstSourceSetControl";
import { SourceViewControl } from "./SourceViewControl";
import {
  conflictNotice,
  orderingAfterMove,
  useImageMutations,
} from "./useImageMutations";
import { useImageReplacement } from "./useImageReplacement";

/** Which control currently owns the in-flight replacement. */
interface ActiveTarget {
  variantId: string;
  imageRole: string;
}

/** The `imageRole` an in-flight FIRST SET is tracked under. It is not a role
 *  the backend ever sees — the set carries the family's real roles — only the
 *  name this screen uses to say which control is busy. */
const INITIAL_SET = "__initial_set__";

/**
 * Images, grouped by colour.
 *
 * Each colour shows two things: the three original views it was built from
 * (which is what an administrator replaces) and the generated outputs the
 * pipeline produced from them (which is what customers see).
 *
 * Only what the backend already exposes safely is rendered — a public URL, a
 * role, a colour, an enabled flag and an order. Storage keys, checksums, source
 * rows, upload batches and signed URLs are never shown.
 */
export function ImagesPanel({
  productId,
  revision,
  editable,
  draftVersion,
  family,
}: {
  productId: string;
  revision: Revision | null;
  /** True only when this revision is the editable draft. */
  editable: boolean;
  draftVersion: number | null;
  /** Which vocabulary this product's source photographs are named in. Read from
   *  the product's own category, never from how the screen was navigated to. */
  family: ProductFamily | null;
}) {
  const replacement = useImageReplacement(productId);
  const [active, setActive] = useState<ActiveTarget | null>(null);
  const slots = sourceSlotsForFamily(family);

  const variants = [...(revision?.color_variants ?? [])].sort(
    (a, b) => a.display_order - b.display_order,
  );

  if (variants.length === 0) {
    return (
      <Panel title="Images">
        <div className="empty">This revision has no colors yet.</div>
      </Panel>
    );
  }

  return (
    <div className="stack">
      {!editable ? (
        <div className="notice notice--info">
          These are the images of the published revision, shown read-only. Open a
          draft to replace an original view or change which images are used —
          the published revision is never edited in place.
        </div>
      ) : null}

      {variants.map((variant) => (
        <VariantImages
          key={variant.id}
          productId={productId}
          variant={variant}
          slots={slots}
          family={family}
          editable={editable}
          draftVersion={draftVersion}
          active={active}
          state={replacement.state}
          onReplace={(file, imageRole) => {
            if (draftVersion === null) return;
            setActive({ variantId: variant.id, imageRole });
            void replacement.start({
              file,
              imageRole,
              variantId: variant.id,
              draftVersion,
            });
          }}
          onFirstSet={(files) => {
            if (draftVersion === null) return;
            setActive({ variantId: variant.id, imageRole: INITIAL_SET });
            void replacement.startInitialSet({
              files,
              variantId: variant.id,
              draftVersion,
            });
          }}
          onDismiss={() => {
            setActive(null);
            replacement.reset();
          }}
        />
      ))}
    </div>
  );
}

function VariantImages({
  productId,
  variant,
  slots,
  family,
  editable,
  draftVersion,
  active,
  state,
  onReplace,
  onFirstSet,
  onDismiss,
}: {
  productId: string;
  variant: ColorVariant;
  slots: readonly ReturnType<typeof sourceSlotsForFamily>[number][];
  family: ProductFamily | null;
  editable: boolean;
  draftVersion: number | null;
  active: ActiveTarget | null;
  state: ReturnType<typeof useImageReplacement>["state"];
  onReplace: (file: File, imageRole: string) => void;
  onFirstSet: (files: { file: File; imageRole: string }[]) => void;
  onDismiss: () => void;
}) {
  const mutations = useImageMutations(productId, draftVersion);
  const [pendingRemoval, setPendingRemoval] = useState<ProductImage | null>(null);
  const byRole = new Map<string, ProductImage>();
  for (const image of variant.images) {
    // Highest version wins as the representative preview for a role.
    const current = byRole.get(image.image_role);
    if (!current || image.version > current.version) byRole.set(image.image_role, image);
  }

  const derivatives = [...variant.images].sort(
    (a, b) => a.display_order - b.display_order || a.version - b.version,
  );

  return (
    <Panel
      title={
        <span className="row">
          <span
            className="swatch"
            style={{ background: variant.swatch_hex }}
            aria-hidden="true"
          />
          <span>{variant.name}</span>
          {!variant.enabled ? (
            <span className="badge badge--neutral">hidden</span>
          ) : null}
        </span>
      }
    >
      <div className="stack">
        {/* First, because for this colour it is the only thing that works. A
            frame colour with nothing yet cannot be given its originals one at a
            time — its card is composed from all three views together, so the
            first set is the unit. Only frames: every other family's
            photographs are independent, and its first one is recorded by the
            ordinary control below. */}
        {family === "glasses" && variant.images.length === 0 ? (
          <FirstSourceSetControl
            variant={variant}
            slots={slots}
            editable={editable}
            active={
              active?.variantId === variant.id &&
              active.imageRole === INITIAL_SET
            }
            state={state}
            onSubmit={onFirstSet}
            onDismiss={onDismiss}
          />
        ) : null}

        <section>
          <h3 className="section-title">Original views</h3>
          <p className="muted section-lead">
            These are the photographs the product is built from. Replacing one
            rebuilds this colour's generated images from the complete set.
          </p>
          <div className="source-view-grid">
            {slots.map((source) => (
              <SourceViewControl
                key={source.imageRole}
                variant={variant}
                label={source.label}
                imageRole={source.imageRole}
                preview={byRole.get(source.previewRole)}
                regenerates={source.regenerates}
                editable={editable}
                active={
                  active?.variantId === variant.id &&
                  active.imageRole === source.imageRole
                }
                state={state}
                onReplace={onReplace}
                onDismiss={onDismiss}
              />
            ))}
          </div>
        </section>

        <section>
          <h3 className="section-title">
            Generated images{" "}
            <span className="workspace__tabCount">{derivatives.length}</span>
          </h3>
          {mutations.error ? (
            <div className="notice notice--error" role="alert">
              {conflictNotice(mutations.error) ??
                (mutations.error instanceof ApiError
                  ? mutations.error.userMessage
                  : "That change could not be applied.")}
            </div>
          ) : null}
          {derivatives.length === 0 ? (
            <p className="muted">No generated images for this color yet.</p>
          ) : (
            <div className="image-grid">
              {derivatives.map((image, index) => (
                <figure
                  className={image.enabled ? "image-card" : "image-card is-disabled-row"}
                  key={image.id}
                >
                  <img
                    className="image-card__thumb"
                    src={image.public_url}
                    alt={`${variant.name} ${image.image_role.replace(/_/g, " ")}`}
                    loading="lazy"
                  />
                  <figcaption className="image-card__body">
                    <span className="image-card__role">
                      {image.image_role.replace(/_/g, " ")}
                    </span>
                    <span className="muted num">
                      {image.width}×{image.height} {image.format} · v{image.version}
                    </span>
                    <span className="muted">
                      {image.enabled ? "Shown" : "Hidden"} · order{" "}
                      {image.display_order}
                    </span>
                  </figcaption>
                  {editable ? (
                    <div className="image-card__actions">
                      <button
                        type="button"
                        className="btn btn--sm btn--subtle"
                        disabled={mutations.busy}
                        aria-label={`${image.enabled ? "Hide" : "Show"} ${
                          variant.name
                        } ${image.image_role.replace(/_/g, " ")}`}
                        onClick={() =>
                          mutations.setEnabled.mutate({
                            imageId: image.id,
                            enabled: !image.enabled,
                          })
                        }
                      >
                        {image.enabled ? "Hide" : "Show"}
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm btn--subtle"
                        disabled={mutations.busy || index === 0}
                        aria-label={`Move ${variant.name} ${image.image_role.replace(
                          /_/g,
                          " ",
                        )} up`}
                        onClick={() => {
                          const ordering = orderingAfterMove(derivatives, image.id, -1);
                          if (ordering) mutations.reorder.mutate(ordering);
                        }}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm btn--subtle"
                        disabled={mutations.busy || index === derivatives.length - 1}
                        aria-label={`Move ${variant.name} ${image.image_role.replace(
                          /_/g,
                          " ",
                        )} down`}
                        onClick={() => {
                          const ordering = orderingAfterMove(derivatives, image.id, 1);
                          if (ordering) mutations.reorder.mutate(ordering);
                        }}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm btn--subtle"
                        disabled={mutations.busy}
                        aria-label={`Remove ${variant.name} ${image.image_role.replace(
                          /_/g,
                          " ",
                        )} from this draft`}
                        onClick={() => setPendingRemoval(image)}
                      >
                        Remove
                      </button>
                    </div>
                  ) : null}
                </figure>
              ))}
            </div>
          )}
        </section>

        {pendingRemoval ? (
          <ConfirmDialog
            title="Remove from this draft?"
            confirmLabel="Remove from this draft"
            destructive
            busy={mutations.remove.isPending}
            onCancel={() => setPendingRemoval(null)}
            onConfirm={() => {
              mutations.remove.mutate(pendingRemoval.id, {
                onSettled: () => setPendingRemoval(null),
              });
            }}
          >
            <p>
              The {pendingRemoval.image_role.replace(/_/g, " ")} image is removed
              from the draft you are editing, so customers will not see it once
              these changes are published.
            </p>
            <p>
              Nothing is deleted. The stored image is retained, and any published
              revision keeps its own copy and its history.
            </p>
          </ConfirmDialog>
        ) : null}
      </div>
    </Panel>
  );
}
